import { useEffect, useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import CrudModal from '../../components/admin/CrudModal';
import { adminDelete, adminGet, adminPost, adminPut } from '../../lib/adminApi';

const emptyLead = {
  company: '',
  contact_name: '',
  phone: '',
  email: '',
  equipment: '',
  service_type: 'reparation',
  source: 'site',
  stage: 'nouveau',
  estimated_amount: 0,
  priority: 'normale',
  next_action: '',
  notes: '',
};

const stageLabels = {
  nouveau: 'Nouveau',
  visite: 'Visite',
  devis_envoye: 'Devis envoyé',
  accord_client: 'Accord client',
  facture: 'Facturé',
};

const stageOrder = Object.keys(stageLabels);

const getTagClassName = (serviceType) => {
  if (serviceType === 'installation') return 'crm-tag crm-tag--installation';
  if (serviceType === 'sav') return 'crm-tag crm-tag--sav';
  if (serviceType === 'peinture') return 'crm-tag crm-tag--paint';
  if (serviceType === 'entretien') return 'crm-tag crm-tag--maintenance';
  return 'crm-tag crm-tag--repair';
};

const AdminPipeline = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formState, setFormState] = useState(emptyLead);
  const [submitting, setSubmitting] = useState(false);

  const fetchLeads = async () => {
    try {
      const data = await adminGet('/admin/leads');
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erreur lors du chargement du pipeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const groupedLeads = useMemo(
    () =>
      stageOrder.map((stage) => ({
        stage,
        label: stageLabels[stage],
        leads: leads.filter((lead) => lead.stage === stage),
      })),
    [leads]
  );

  const openCreateModal = () => {
    setEditingLead(null);
    setFormState(emptyLead);
    setModalOpen(true);
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setFormState({
      ...emptyLead,
      ...lead,
    });
    setModalOpen(true);
  };

  const handleChange = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formState,
        estimated_amount: Number(formState.estimated_amount || 0),
      };

      if (editingLead) {
        await adminPut(`/admin/leads/${editingLead.id}`, payload);
        toast.success('Opportunité mise à jour');
      } else {
        await adminPost('/admin/leads', payload);
        toast.success('Opportunité créée');
      }
      setModalOpen(false);
      fetchLeads();
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Supprimer cette opportunité ?')) return;
    try {
      await adminDelete(`/admin/leads/${leadId}`);
      toast.success('Opportunité supprimée');
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="crm-loading">
          <div className="crm-spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout onAction={openCreateModal}>
      <Toaster position="top-right" richColors />

      <section className="crm-card">
        <div className="crm-section-head">
          <div>
            <h2 className="crm-section-title">Pipeline CRM</h2>
            <p className="crm-section-subtitle">
              Gérez les opportunités, du premier contact jusqu’à la facturation.
            </p>
          </div>
        </div>

        <div className="crm-pipeline-grid">
          {groupedLeads.map((column) => (
            <div key={column.stage} className="crm-pipeline-column">
              <div className="crm-pipeline-column__head">
                <span>{column.label}</span>
                <span className="crm-pill-count">{column.leads.length}</span>
              </div>

              <div className="crm-pipeline-column__body">
                {column.leads.length === 0 ? (
                  <div className="crm-empty">Aucune opportunité</div>
                ) : (
                  column.leads.map((lead) => (
                    <article key={lead.id} className="crm-pipeline-card">
                      <div className="crm-pipeline-card__name">{lead.company}</div>
                      <div className="crm-pipeline-card__type">
                        {lead.contact_name} · {lead.equipment || 'Engin non précisé'}
                      </div>
                      <span className={getTagClassName(lead.service_type)}>
                        {lead.service_type}
                      </span>
                      <div className="crm-pipeline-card__amount">
                        {Math.round(Number(lead.estimated_amount || 0)).toLocaleString('fr-FR')} €
                      </div>
                      <div className="crm-table__actions" style={{ marginTop: '10px' }}>
                        <button type="button" className="crm-action-link" onClick={() => openEditModal(lead)}>
                          Modifier
                        </button>
                        <button type="button" className="crm-action-danger" onClick={() => handleDelete(lead.id)}>
                          Supprimer
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {modalOpen ? (
        <CrudModal
          title={editingLead ? 'Modifier une opportunité' : 'Créer une opportunité'}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={editingLead ? 'Mettre à jour' : 'Créer'}
        >
          <div className="crm-form__grid">
            <div className="crm-form__field">
              <label>Entreprise</label>
              <input value={formState.company} onChange={(e) => handleChange('company', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Contact</label>
              <input value={formState.contact_name} onChange={(e) => handleChange('contact_name', e.target.value)} required />
            </div>
            <div className="crm-form__field">
              <label>Téléphone</label>
              <input value={formState.phone} onChange={(e) => handleChange('phone', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Email</label>
              <input type="email" value={formState.email} onChange={(e) => handleChange('email', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Engin</label>
              <input value={formState.equipment} onChange={(e) => handleChange('equipment', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Montant estimé</label>
              <input type="number" min="0" value={formState.estimated_amount} onChange={(e) => handleChange('estimated_amount', e.target.value)} />
            </div>
            <div className="crm-form__field">
              <label>Service</label>
              <select value={formState.service_type} onChange={(e) => handleChange('service_type', e.target.value)}>
                <option value="reparation">Réparation</option>
                <option value="peinture">Peinture</option>
                <option value="entretien">Entretien</option>
                <option value="installation">Installation</option>
                <option value="sav">SAV</option>
              </select>
            </div>
            <div className="crm-form__field">
              <label>Étape</label>
              <select value={formState.stage} onChange={(e) => handleChange('stage', e.target.value)}>
                {stageOrder.map((stage) => (
                  <option key={stage} value={stage}>{stageLabels[stage]}</option>
                ))}
              </select>
            </div>
            <div className="crm-form__field">
              <label>Source</label>
              <select value={formState.source} onChange={(e) => handleChange('source', e.target.value)}>
                <option value="site">Site</option>
                <option value="client">Client</option>
                <option value="recommandation">Recommandation</option>
                <option value="appel">Appel entrant</option>
              </select>
            </div>
            <div className="crm-form__field">
              <label>Priorité</label>
              <select value={formState.priority} onChange={(e) => handleChange('priority', e.target.value)}>
                <option value="basse">Basse</option>
                <option value="normale">Normale</option>
                <option value="haute">Haute</option>
              </select>
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Prochaine action</label>
              <input value={formState.next_action} onChange={(e) => handleChange('next_action', e.target.value)} />
            </div>
            <div className="crm-form__field crm-form__field--full">
              <label>Notes</label>
              <textarea value={formState.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </div>
          </div>
        </CrudModal>
      ) : null}
    </AdminLayout>
  );
};

export default AdminPipeline;
